import React from "react";
import {createContext, useContext, useEffect, useLayoutEffect, useReducer} from 'react';
import {useApi} from "../hooks/useApi.jsx";
import {useLocalStorage} from "../hooks/useStorage.jsx";

const AuthContext = createContext();

// Auth reducer
const authReducer = (state, action) => {
    switch (action.type) {
        case 'SET_AUTHENTICATED':
            return {
                ...state,
                status: 'authenticated',
                token: action.payload.token,
                user: action.payload.user
            };
        case 'SET_UNAUTHENTICATED':
            return {
                ...state,
                status: 'not-authenticated',
                token: null,
                user: null
            };
        case 'TOKEN_REFRESH_SUCCESS':
            return {
                ...state,
                token: action.payload.token
            };
        case 'UPDATE_USER':
            return {
                ...state,
                user: action.payload.user
            };
        default:
            return state;
    }
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within a AuthContext provider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [authState, dispatch] = useReducer(authReducer, {
        status: 'initializing',
        token: null,
        user: null
    });

    const {api, callApi} = useApi();
    const [storedUser, setStoredUser, removeStoredUser] = useLocalStorage('user', null);


    // Clean action methods
    const setAuthenticated = (token, user) => {
        setStoredUser(user);
        dispatch({
            type: 'SET_AUTHENTICATED',
            payload: { token, user }
        });
    };

    const setUnauthenticated = () => {
        removeStoredUser();
        dispatch({ type: 'SET_UNAUTHENTICATED' });
    };

    const login = async (username, password) => {
        try {
            const response = await callApi({
                request: async () => {
                    return await api.login(username, password);
                },
                bubbleError: true,
            });
            setAuthenticated(response.jwt.accessToken, response.userContext);
        } catch (e) {
            console.error(e)
            setUnauthenticated();
        }
    };

    const logout = async () => {
        try {
            await callApi({
                request: async () => {
                    return await api.logout()
                }
            });
        } catch (e) {
            console.error(e)
        } finally {
            // always runs, even on error.
            setUnauthenticated();
        }
    };

    const updateToken = (token) => {
        dispatch({
            type: 'TOKEN_REFRESH_SUCCESS',
            payload: { token }
        });
    };

    const updateUser = (user) => {
        // todo
        setStoredUser(user);
        dispatch({
            type: 'UPDATE_USER',
            payload: { user }
        });
    };

    // Initialize auth once on mount
    useEffect(() => {
        const init = async () => {
            try {
                const response = await callApi({
                    request: async () => {
                        return await api.refreshAccessToken();
                    },
                    bubbleError: true,
                    showError: false,
                });

                setAuthenticated(response.data.accessToken, storedUser);
            } catch (error) {
                // If we had stored user data but refresh failed,
                // we should probably call logout to clean up the refresh token
                if (storedUser) {
                    await logout();
                } else {
                    setUnauthenticated();
                }
            }
        };
        init();
    }, []);

    // attaches auth token to outgoing requests (if it exists).
    useLayoutEffect(() => {
        const authInterceptor = api.gateway.interceptors.request.use((config) => {
            config.headers.Authorization =
                !config._retry && authState.token
                    ? `Bearer ${authState.token}`
                    : config.headers.Authorization;
            return config;
        });

        return () => {
            api.gateway.interceptors.request.eject(authInterceptor);
        };
    }, [authState.token]);

    useLayoutEffect(() => {
        // if incoming response is 401 (unauthorized) (potentially due to browser refresh, or access token expiration)
        // than we attempt to refresh our access token.

        const refreshInterceptor = api.gateway.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                if (
                    error.response.status === 401 &&
                    error.response.data.error === 'Unauthorized' &&
                    originalRequest._retry !== true &&
                    !originalRequest.url.includes("/jwt/refresh")
                ) {
                    try {

                        originalRequest._retry = true; // prevent infinite loop
                        const response = await callApi({
                            request: async () => {
                                return await api.refreshAccessToken();
                            },
                            bubbleError: true,
                        });

                        updateToken(response.data.accessToken);

                        originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;

                        return await api.gateway(originalRequest);
                    } catch {
                        await logout();
                    }
                }
                return Promise.reject(error);
            },
        );

        return () => {
            api.gateway.interceptors.response.eject(refreshInterceptor);
        };
    }, []);

    return (
        <AuthContext.Provider value={{
            // State
            status: authState.status,
            user: authState.user,

            // Actions
            login,
            logout,
            updateUser,

            // Convenience getters
            userIsAuthenticated: authState.status === 'authenticated',
            authIsInitializing: authState.status === 'initializing',
        }}>
            {children}
        </AuthContext.Provider>
    );
};